import React from "react";
import Typography from "@pagepro-monorepo/ui/lib/components/styles/Typography";
import ButtonLink from "@pagepro-monorepo/ui/lib/components/atoms/Button/ButtonLink";

import IconArrowRight from "@assets/svg/arrow-right.svg";

import { AssessmentResult } from "./types";
import * as Styled from "./styles";
import { RISK_COLORS } from "./styles";

interface ResultsPhaseProps {
  result: AssessmentResult;
  onRestart: () => void;
}

const STAGE_COLORS: Record<
  AssessmentResult["stage"],
  { border: string; bg: string }
> = {
  "pre-icp": { border: "#B7B7B780", bg: "#F3F7F8" },
  "stage-1": {
    border: `${RISK_COLORS.medium}40`,
    bg: `${RISK_COLORS.medium}08`
  },
  "stage-2": {
    border: `${RISK_COLORS.high}40`,
    bg: `${RISK_COLORS.high}08`
  },
  "stage-3": {
    border: `${RISK_COLORS.low}40`,
    bg: `${RISK_COLORS.low}08`
  }
};

const ResultsPhase: React.FC<ResultsPhaseProps> = ({
  result,
  onRestart
}) => {
  const stageColors = STAGE_COLORS[result.stage];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem"
      }}
    >
      {/* Stage + Risks merged */}
      <Styled.ResultCard
        $borderColor={stageColors.border}
        $bgColor={stageColors.bg}
      >
        <Styled.SectionLabel style={{ marginBottom: "0.25rem" }}>
          Your Current Stage
        </Styled.SectionLabel>
        <Typography variant="heading4" as="h2" mb="1rem">
          {result.stageLabel}
        </Typography>
        <Styled.NarrowText>
          <Typography variant="body2" as="p">
            {result.explanation}
          </Typography>
        </Styled.NarrowText>

        <div
          style={{
            borderTop: `1px solid ${stageColors.border}`,
            paddingTop: "1.5rem"
          }}
        >
          <Styled.SectionLabel style={{ marginBottom: "1rem" }}>
            Identified Risk Areas
          </Styled.SectionLabel>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {result.risks.map((risk) => (
              <Styled.RiskRow key={risk.category}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    marginBottom: "0.25rem"
                  }}
                >
                  <Styled.RiskCategoryLabel>
                    {risk.category}
                  </Styled.RiskCategoryLabel>
                  <Styled.RiskBadge $severity={risk.severity}>
                    {risk.severity}
                  </Styled.RiskBadge>
                </div>
                <Typography variant="body5" as="p">
                  {risk.explanation}
                </Typography>
              </Styled.RiskRow>
            ))}
          </div>
        </div>
      </Styled.ResultCard>

      {/* Recommended Path — dark CTA card */}
      <Styled.CtaCard>
        <Styled.SectionLabel
          style={{
            marginBottom: "0.75rem",
            color: "inherit",
            opacity: 0.6
          }}
        >
          Recommended Next Step
        </Styled.SectionLabel>
        <Typography
          variant="heading4"
          as="h3"
          mb="0.75rem"
          color="inherit"
        >
          {result.recommendedPath.name}
        </Typography>
        <Styled.NarrowText>
          <Typography variant="body2" as="p" color="inherit">
            {result.recommendedPath.description}
          </Typography>
        </Styled.NarrowText>
        <div
          style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}
        >
          <ButtonLink
            href={result.recommendedPath.ctaUrl}
            icon={IconArrowRight}
          >
            {result.recommendedPath.cta}
          </ButtonLink>
          {result.recommendedPath.secondaryCta && (
            <ButtonLink
              href={result.recommendedPath.secondaryCtaUrl}
              $mode="secondary"
            >
              {result.recommendedPath.secondaryCta}
            </ButtonLink>
          )}
        </div>
      </Styled.CtaCard>

      {/* Restart */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          paddingTop: "1rem",
          paddingBottom: "1rem"
        }}
      >
        <Styled.RestartButton type="button" onClick={onRestart}>
          Retake Assessment
        </Styled.RestartButton>
      </div>
    </div>
  );
};

export default ResultsPhase;
